import { ACTIVITY_LEVELS, ACTIVITY_MAX_STEPS_PER_LEVEL, activityStepsPath, type ActivityLevel } from '@sparkapp/shared-types';
import PointsList from './PointsList';
import type { ActivityPointForm } from '../../types/activity';
import type { ActivityFieldErrors } from './validateActivityForm';

const LEVEL_DISPLAY: Record<ActivityLevel, { label: string; bg: string; color: string }> = {
  easier: { label: 'Easier / Step Down', bg: '#DCEFE7', color: '#28564A' },
  default: { label: 'Default', bg: '#B7DFD0', color: '#1F4C40' },
  harder: { label: 'Harder / Step Up', bg: '#5FBF9A', color: '#ffffff' },
};

interface DifficultyLevelsProps {
  levels: Record<ActivityLevel, ActivityPointForm[]>;
  onChange: (level: ActivityLevel, points: ActivityPointForm[]) => void;
  errors: ActivityFieldErrors;
  readOnly?: boolean;
}

export default function DifficultyLevels({ levels, onChange, errors, readOnly }: DifficultyLevelsProps) {
  return (
    <div data-testid="difficulty-levels">
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-pine-pine mb-[8px]">
        The activity <span className="text-pine-danger">*</span>
      </p>
      <div className="border-[1.5px] border-pine-line rounded-[12px] p-[16px] grid grid-cols-1 lg:grid-cols-3 gap-[16px]">
        {ACTIVITY_LEVELS.map(({ level }) => {
          const display = LEVEL_DISPLAY[level];
          const points = levels[level] ?? [];
          return (
            <div key={level} className="min-w-0">
              <div
                className="w-full rounded-full px-3 py-1.5 text-center text-[13px] font-bold mb-[12px]"
                style={{ backgroundColor: display.bg, color: display.color }}
              >
                {display.label}
              </div>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mb-[8px]">
                How to play
              </p>
              {readOnly ? (
                <ol className="list-decimal pl-5 text-[13px] text-pine-ink space-y-1" data-testid={`level-${level}-points`}>
                  {points.map((p) => (
                    <li key={p._id}>{p.text}</li>
                  ))}
                </ol>
              ) : (
                <PointsList<ActivityPointForm>
                  points={points}
                  onChange={(next) => onChange(level, next)}
                  error={errors[activityStepsPath(level)]}
                  max={ACTIVITY_MAX_STEPS_PER_LEVEL}
                  testId={`level-${level}-points`}
                />
              )}
            </div>
          );
        })}
      </div>
      {errors.steps && <p className="text-[11px] text-pine-danger mt-[6px]">{errors.steps}</p>}
    </div>
  );
}
